import { ModalStep } from '@alltrails/modal';
import Button from '@alltrails/denali/components/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import logInstallPromptClicked from '@alltrails/amplitude/events/logInstallPromptClicked';
import { useExperiments } from '@alltrails/experiments';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logContinueInBrowserClicked from '@alltrails/amplitude/events/logContinueInBrowserClicked';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import useDeeplink from '../../../hooks/useDeeplink';
import getBrowserData from '../../../utils/getBrowserData';
import styles from './BaseInterstitialStep.module.scss';

type BaseInterstitialStepProps = {
  children: React.ReactNode;
  contentsClassName?: string;
  deeplinkData?: DeeplinkData;
  displayTrigger: CarouselDisplayTrigger;
  navigateToAuthStep: () => void;
  onCloseRequest: () => void;
};

const BaseInterstitialStep = ({
  onCloseRequest,
  deeplinkData,
  navigateToAuthStep,
  children,
  contentsClassName,
  displayTrigger
}: BaseInterstitialStepProps) => {
  const getDeeplink = useDeeplink({ deeplinkData, isOpen: true, promptType: 'interstitial', displayTrigger });
  const intl = useIntl();
  const browserName = useSelector((state: { browser?: UserAgentBrowser }) => state.browser?.name);
  const { buttonText: browserButtonText } = getBrowserData(intl, browserName);
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';

  const handleContinueInBrowserClick = () => {
    logContinueInBrowserClicked({
      install_prompt_type: 'interstitial',
      install_prompt_location: 'trail page', // This field is invalid for now since InstallPromptLocation type isn't accessed globally. Analytics team ignores this field.
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'branch'
    });

    navigateToAuthStep();
  };

  const handleContinueInAppClick = () => {
    logInstallPromptClicked({
      install_prompt_type: 'interstitial',
      install_prompt_location: 'trail page', // This field is invalid for now since InstallPromptLocation type isn't accessed globally. Analytics team ignores this field.
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'branch'
    });

    window.location.assign(getDeeplink());
  };

  const handleCloseRequest = () => {
    logInstallPromptDismissed({
      install_prompt_type: 'interstitial',
      install_prompt_location: 'trail page', // This field is invalid for now since InstallPromptLocation type isn't accessed globally. Analytics team ignores this field.
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'branch'
    });
    onCloseRequest();
  };

  return (
    <ModalStep onCloseRequest={handleCloseRequest} contentsClassName={contentsClassName}>
      {children}
      <div className={styles.buttons}>
        <Button
          width="fullOnMobile"
          variant="primary"
          text={<FormattedMessage defaultMessage="Continue in app" />}
          testId="close-interstitial"
          onClick={handleContinueInAppClick}
        />
        <Button width="fullOnMobile" variant="default" text={browserButtonText} onClick={handleContinueInBrowserClick} testId="continue-in-browser" />
      </div>
    </ModalStep>
  );
};

export default BaseInterstitialStep;
